import type { ICollectionItem } from '@/types';
import type { IFandom } from '@/features/fanfic-page/types/fanfic';
import type { PostStatusType } from '@/types/fanfic-status';
import type { ProgressResponse } from '@/hooks/use-reading-v2';
import type { FanficCardCharacter } from '@/features/fanfic-card/types/fanfic-card-character.types';
import { resolveFandomCardFields } from '@/features/fanfic-card/utils/resolveFandomCardFields.util';
import { mapCharactersToCardFields } from '@/features/fanfic-card/utils/resolveCharacterCardFields.util';
import { getRandomPlaceholder } from '@/lib/fanfic-placeholders';
import { resolveFanficPublishedAt } from '@/features/fanfic-card/utils/resolve-fanfic-published-at.util';
import { formatFanficDate } from './format-fanfic-date.util';

// Interface for displaying fanfic in grid
export interface FanficDisplay {
  id: number;
  title: string;
  description?: string;
  author?: string;
  rating?: string;
  chaptersCount?: number;
  score?: number;
  direction?: string;
  likes?: number;
  views?: number;
  date?: string;
  status?: string;
  cover?: string;
  createdAt?: string;
  publishedAt?: string;
  updatedAt?: string;
  userLiked?: boolean;
  type?: 'fanfic' | 'original';
  tags?: string[];
  characters?: FanficCardCharacter[];
  fandom?: string;
  fandomId?: string;
  fandomPermanentUrl?: string;
  postStatus?: PostStatusType;
  size?: string;
  totalWordCount?: number;
  pairings?: any[];
  additionalFandoms?: IFandom[];
  readingProgress: ProgressResponse[];
  collectionItem?: ICollectionItem;
  focus: string;
  price?: number | null;
  promoCampaignId?: number | null;
}

/**
 * Transforms API fanfic data to display format for the grid
 */
export const transformFanficData = (fanfic: any): FanficDisplay => {
  const postStatus = fanfic['post-status'] || fanfic.postStatus;
  const fandomFields = resolveFandomCardFields(fanfic.fandom, fanfic.universe);
  const fandomSlug = typeof fanfic.fandom?.slug === 'string' ? fanfic.fandom.slug : undefined;

  return {
    id: fanfic.id,
    title: fanfic.title || 'Без названия',
    description: fanfic.description || 'Без описания',
    author: fanfic.author?.name || fanfic.userId || 'Неизвестный автор',
    rating: fanfic.rating || 'G',
    chaptersCount: fanfic?.chaptersCount || fanfic.chapters?.length,
    score: fanfic.score || 0,
    direction: fanfic.direction || '♂ Гет',
    likes: fanfic.likesCount || 0,
    views: fanfic.readersCount || 0,
    date: (() => {
      const publishedAt = resolveFanficPublishedAt(fanfic);
      return publishedAt ? formatFanficDate(publishedAt) : undefined;
    })(),
    status: postStatus || 'В процессе',
    cover: fanfic?.heroImage?.url || getRandomPlaceholder(fanfic.id),
    createdAt: fanfic.createdAt,
    publishedAt: fanfic.publishedAt,
    updatedAt: fanfic.updatedAt,
    userLiked: fanfic.userLiked || false,
    type: fanfic.type || 'fanfic',
    tags: fanfic.tags?.map((tag: any) => tag.title || tag) || [],
    characters: mapCharactersToCardFields(fanfic.characters, fandomSlug),
    fandom: fandomFields.title,
    fandomId: fandomFields.id,
    fandomPermanentUrl: fandomFields.permanentUrl,
    postStatus,
    size: fanfic.size,
    totalWordCount: fanfic.totalWordCount,
    pairings: fanfic.pairings,
    additionalFandoms: fanfic.additionalFandoms,
    readingProgress: fanfic.readingProgress,
    collectionItem: fanfic.collectionItem,
    focus: fanfic.focus,
    price: fanfic.price ?? null,
    promoCampaignId: fanfic.promoCampaignId ?? null,
  };
};
